import { cn, cva, type VariantProps } from 'noddi-ui-common';
import { PropsWithChildren } from 'react';
import { NoddiBasicCard } from '../Cards/NoddiBasicCard';
import { contentMaxWidthVariants, type LayoutContainerWidth } from './layoutContainer';

const wrapperVariants = cva('fixed inset-x-0 bottom-0 z-docked px-0 md:bottom-4', {
  variants: {
    width: {
      narrow: 'md:px-4',
      page: 'md:px-5 xl:px-6'
    }
  },
  defaultVariants: {
    width: 'narrow'
  }
});

// Phones: a soft upward shadow hints that content scrolls on underneath the docked bar.
const cardVariants = cva(
  'mx-auto w-full p-4 max-md:rounded-t-2xl max-md:rounded-b-none max-md:shadow-[0_-8px_24px_-6px_rgb(0_0_0/0.14)] md:px-10',
  {
    variants: {
      width: {
        narrow: 'max-w-screen-lg md:shadow-xl',
        page: 'max-w-screen-3xl md:shadow-2xl'
      }
    },
    defaultVariants: {
      width: 'narrow'
    }
  }
);

// The page container's gutter from `md`; phones keep the edge-to-edge bar.
const contentGutterVariants = cva('', {
  variants: {
    contentWidth: {
      md: 'md:px-10',
      lg: 'md:px-10',
      xl: 'md:px-10',
      xxl: 'md:px-10 xl:px-4',
      wide: 'md:px-4',
      full: ''
    } satisfies Record<LayoutContainerWidth, string>
  }
});

type Props = VariantProps<typeof wrapperVariants> & {
  className?: string;
  /**
   * From `md`, lines the bar up with a `ScreenBaseWrapper` of this width instead of `width`'s fixed size,
   * so the bar's edges meet the page content's.
   */
  contentWidth?: LayoutContainerWidth;
};

export const FixedBottomBarContainer = ({
  children,
  className,
  width = 'narrow',
  contentWidth
}: PropsWithChildren<Props>) => {
  return (
    <div className={cn(wrapperVariants({ width }), contentWidth && 'md:px-0', className)}>
      <div
        className={cn(
          contentWidth && [contentGutterVariants({ contentWidth }), contentMaxWidthVariants({ width: contentWidth })]
        )}
      >
        <NoddiBasicCard className={cn(cardVariants({ width }), contentWidth && 'md:max-w-none')}>
          <div className='flex items-center justify-between gap-2'>{children}</div>
        </NoddiBasicCard>
      </div>
    </div>
  );
};
